// Disease-filter matching. [ADM.046] replaced the prototype's question-based `qMatchesFilter` with
// `itemMatchesFilter`: the criterion is the **item's Scope**, matching PRD 06 §3.1. The filter value
// can be empty (= 전체), 'COMMON', a 'GRP:<groupName>' tag, or an ICD code string (e.g. 'F32'). The
// disease lookup tables map the numeric `diseaseGroupId` / `diseaseCodeId` back to the values the
// dropdown uses.

import type {
  DiseaseCode,
  DiseaseGroup,
} from '@/features/disease-code/types/disease-code.types';

import type { ProTreeNode } from '@/features/pro-tree/types/pro-tree.types';

import type { DiseaseFilterValue } from '../store/questionnaire-design-page.store';

export interface DiseaseLookup {
  groupById: Map<number, DiseaseGroup>;
  codeById: Map<number, DiseaseCode>;
}

export function buildDiseaseLookup(
  groups: DiseaseGroup[],
  codesByGroup: Map<number, DiseaseCode[]>,
): DiseaseLookup {
  const groupById = new Map<number, DiseaseGroup>();
  for (const g of groups) groupById.set(g.id, g);

  const codeById = new Map<number, DiseaseCode>();
  for (const list of codesByGroup.values()) {
    for (const c of list) codeById.set(c.id, c);
  }

  return { groupById, codeById };
}

/**
 * Does this PRO **item** fall inside the disease filter? — [ADM.046], PRD 05 v1.29 §3.1.
 *
 * The matching criterion is the **item's Scope**, the single axis PRD 06 §3.1 already used. A
 * question does not carry its own 적용범위 any more; it inherits the item's (SHCT-517), so testing the
 * question would ask the same thing twice and could answer differently on legacy rows whose stored
 * `applicability` drifted from their item's.
 *
 * Per [ADM.047] this deliberately does **not** look at how many questions the item holds — an item
 * whose Scope matches stays highlighted and clickable at zero questions, so the first one can be
 * written. An item whose Scope does not match is dimmed no matter how many it has ([ADM.023]).
 */
export function itemMatchesDiseaseFilter(
  item: ProTreeNode,
  filter: DiseaseFilterValue,
  lookup: DiseaseLookup,
): boolean {
  if (!filter) return true; // 전체

  // A server build older than the [ADM.046] field returns no scope. Treat the item as visible rather
  // than dimming the whole tree — hiding everything reads as a broken screen, and the deployment
  // screen already refuses to publish an item with no scope.
  if (!item.scope) return true;

  if (filter === 'COMMON') return item.scope === 'COMMON';

  if (filter.startsWith('GRP:')) {
    const groupName = filter.slice(4);
    if (item.scope === 'COMMON') return true;
    if (item.scope === 'DISEASE_GROUP' && item.diseaseGroupId != null) {
      return lookup.groupById.get(item.diseaseGroupId)?.name === groupName;
    }
    return false;
  }

  // Otherwise the filter is an ICD code (e.g. 'F32').
  if (item.scope === 'COMMON') return true;
  if (item.scope === 'DISEASE_SPECIFIC' && item.diseaseCodeId != null) {
    return lookup.codeById.get(item.diseaseCodeId)?.code === filter;
  }
  return false;
}
